import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDesktopAnalytics } from '../api/desktopAnalytics';
import type {
  DesktopAnalyticsData,
  DesktopBreakdownEntry,
  DesktopMultiSeries,
  DesktopSeries,
} from '../types/desktopAnalytics';
import styles from './DesktopAnalytics.module.css';

/**
 * Studio Desktop: installs, active installs, versions, downloads, updates and
 * crashes, from the app's anonymous telemetry plus the download redirect.
 *
 * "Active" = sent its daily heartbeat (client-local date). An install that
 * opted out stops heartbeating, so it drops out of "active" after its last
 * day; the opt-out count in the summary is exact (the app sends one final
 * event when the switch is turned off).
 */

const OS_LABELS: Record<string, string> = {
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  unknown: 'Unknown',
};

const OS_COLORS: Record<string, string> = {
  macos: '#a855f7',
  windows: '#3b82f6',
  linux: '#f59e0b',
  unknown: '#6b7280',
};

const PLATFORM_LABELS: Record<string, string> = {
  'mac-arm64': 'macOS (Apple silicon)',
  'mac-x64': 'macOS (Intel)',
  'win-x64': 'Windows',
};

const PLATFORM_COLORS: Record<string, string> = {
  'mac-arm64': '#a855f7',
  'mac-x64': '#c084fc',
  'win-x64': '#3b82f6',
};

const MODE_COLORS: Record<string, string> = {
  local: '#6b7280',
  synced: '#22c55e',
};

const TOOLTIP_STYLE = {
  background: '#111827',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: 12,
};

const DAY_OPTIONS = [7, 14, 30, 90, 180, 365];

function shortDate(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function pct(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 1000) / 10 : 0;
}

function tickInterval(n: number): number {
  if (n <= 14) return 0;
  if (n <= 31) return 3;
  if (n <= 90) return 9;
  return Math.ceil(n / 12);
}

function SeriesChart({
  title, series, color, note, gradientId,
}: { title: string; series: DesktopSeries; color: string; note?: string; gradientId: string }) {
  const points = useMemo(
    () => series.labels.map((label, i) => ({ label: shortDate(label), value: series.data[i] ?? 0 })),
    [series],
  );
  const total = series.data.reduce((a, b) => a + b, 0);
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        <span className={styles.chartTotal}>{total.toLocaleString()} in window</span>
      </div>
      <div className={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} interval={tickInterval(points.length)} />
            <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#e5e7eb' }} />
            <Area type="monotone" dataKey="value" name={title} stroke={color} strokeWidth={2} fill={`url(#${gradientId})`} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {note && <div className={styles.chartNote}>{note}</div>}
    </div>
  );
}

function DownloadsChart({ series }: { series: DesktopMultiSeries }) {
  const keys = Object.keys(series.series);
  const points = useMemo(
    () => series.labels.map((label, i) => {
      const row: Record<string, string | number> = { label: shortDate(label) };
      for (const k of keys) row[k] = series.series[k][i] ?? 0;
      return row;
    }),
    [series, keys],
  );
  const total = keys.reduce((sum, k) => sum + series.series[k].reduce((a, b) => a + b, 0), 0);
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>Downloads by platform</h3>
        <span className={styles.chartTotal}>{total.toLocaleString()} in window</span>
      </div>
      <div className={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#9ca3af" tick={{ fontSize: 11 }} interval={tickInterval(points.length)} />
            <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#e5e7eb' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys.map((k) => (
              <Bar key={k} dataKey={k} name={PLATFORM_LABELS[k] || k} stackId="dl" fill={PLATFORM_COLORS[k] || '#6b7280'} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.chartNote}>
        Only downloads through the redirect link are counted; auto-updates fetch S3 directly. Bots excluded.
      </div>
    </div>
  );
}

function Breakdown({
  title, rows, total, colors, labels, empty,
}: { title: string; rows: DesktopBreakdownEntry[]; total: number; colors?: Record<string, string>; labels?: Record<string, string>; empty: string }) {
  return (
    <div className={styles.breakdownCard}>
      <h3 className={styles.breakdownTitle}>{title}</h3>
      {rows.length === 0 ? (
        <p className={styles.emptyNotice}>{empty}</p>
      ) : (
        rows.map((row) => {
          const p = pct(row.count, total);
          const color = colors?.[row.key] || '#3b82f6';
          return (
            <div key={row.key} className={styles.breakdownRow}>
              <span className={styles.breakdownLabel}>
                <span className={styles.dot} style={{ background: color }} />
                {labels?.[row.key] || row.key}
              </span>
              <span className={styles.breakdownCount}>{row.count.toLocaleString()}</span>
              <span className={styles.breakdownPct}>{p}%</span>
              <div className={styles.pctBarTrack}>
                <div className={styles.pctBarFill} style={{ width: `${Math.min(p, 100)}%`, background: color }} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

export default function DesktopAnalytics() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<DesktopAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDesktopAnalytics(days));
    } catch {
      setError('Failed to load desktop analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const aliveTotal = data ? data.by_version.reduce((a, r) => a + r.count, 0) : 0;
  const dlTotal = data ? data.downloads_by_platform.reduce((a, r) => a + r.count, 0) : 0;
  const applyRate = data && data.updates.downloaded > 0
    ? Math.round((data.updates.applied / data.updates.downloaded) * 100)
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Desktop App</h1>
          <p className={styles.subtitle}>Studio for macOS and Windows: installs, versions, downloads, updates and crashes</p>
        </div>
        <div className={styles.headerControls}>
          <select className={styles.periodSelect} value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAY_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
          <button className={styles.refreshButton} onClick={load} disabled={loading}>
            <span className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && !data ? (
        <div className={styles.loadingSmall}>Loading...</div>
      ) : data && s ? (
        <>
          {/* ===== INSTALLED BASE ===== */}
          <div className={styles.statCards}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Total installs</div>
              <div className={styles.statValue}>{s.total_installs.toLocaleString()}</div>
              <div className={styles.statSub}>{s.new_installs.toLocaleString()} new in window</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Active today</div>
              <div className={styles.statValue}>{s.active_today.toLocaleString()}</div>
              <div className={styles.statSub}>{s.active_7d.toLocaleString()} / 7d · {s.active_30d.toLocaleString()} / 30d</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Synced installs</div>
              <div className={styles.statValue}>{s.synced_installs.toLocaleString()}</div>
              <div className={styles.statSub}>{s.linked_accounts.toLocaleString()} linked accounts</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Downloads</div>
              <div className={styles.statValue}>{s.downloads.toLocaleString()}</div>
              <div className={styles.statSub}>via redirect link, in window</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Opted out</div>
              <div className={styles.statValue}>{s.opted_out.toLocaleString()}</div>
              <div className={styles.statSub}>{pct(s.opted_out, s.total_installs)}% of installs</div>
            </div>
          </div>

          <div className={styles.statCards}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Sessions</div>
              <div className={styles.statValue}>{s.sessions.toLocaleString()}</div>
              <div className={styles.statSub}>launches in window</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Crashes</div>
              <div className={`${styles.statValue} ${s.crashes > 0 ? styles.statBad : ''}`}>{s.crashes.toLocaleString()}</div>
              <div className={styles.statSub}>
                {s.sessions > 0 ? `${(s.crashes / s.sessions * 1000).toFixed(1)} per 1,000 sessions` : 'renderer crashes'}
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>JS errors</div>
              <div className={styles.statValue}>{s.errors.toLocaleString()}</div>
              <div className={styles.statSub}>uncaught, hashed</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Updates applied</div>
              <div className={styles.statValue}>{data.updates.applied.toLocaleString()}</div>
              <div className={styles.statSub}>
                {data.updates.downloaded.toLocaleString()} downloaded{applyRate != null ? ` · ${applyRate}% applied` : ''}
                {data.updates.errors > 0 ? ` · ${data.updates.errors} errors` : ''}
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Card importers</div>
              <div className={styles.statValue}>{s.card_importers.toLocaleString()}</div>
              <div className={styles.statSub}>installs that imported a BYOK card</div>
            </div>
          </div>

          {/* ===== TRENDS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Trends</h2>
            <div className={styles.chartGrid}>
              <SeriesChart title="Active installs" series={data.daily.active_installs} color="#22c55e" gradientId="dt-active"
                note="Installs that sent their daily heartbeat (client-local date)." />
              <SeriesChart title="New installs" series={data.daily.new_installs} color="#3b82f6" gradientId="dt-new"
                note="First time an install id was seen." />
              <DownloadsChart series={data.daily.downloads} />
              <SeriesChart title="Crashes + JS errors" series={data.daily.crashes} color="#ef4444" gradientId="dt-crash" />
            </div>
          </section>

          {/* ===== BREAKDOWNS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Installed base</h2>
            <div className={styles.breakdownGrid}>
              <Breakdown title="By OS" rows={data.by_os} total={s.total_installs} colors={OS_COLORS} labels={OS_LABELS} empty="No installs yet" />
              <Breakdown title="By version (active last 30d)" rows={data.by_version} total={aliveTotal} empty="No active installs yet" />
              <Breakdown title="By mode" rows={data.by_mode} total={s.total_installs} colors={MODE_COLORS} empty="No installs yet" />
              <Breakdown title="By country" rows={data.by_country} total={s.total_installs} empty="No country data (needs a CDN country header)" />
              <Breakdown title="Downloads by platform" rows={data.downloads_by_platform} total={dlTotal} colors={PLATFORM_COLORS} labels={PLATFORM_LABELS} empty="No downloads in window" />
              <Breakdown title="Downloads by source" rows={data.downloads_by_source} total={dlTotal} empty="No downloads in window" />
            </div>
            <div className={styles.inlineStats}>
              <span>Mode changes in window: <strong>{data.mode_changes.to_synced}</strong> turned sync on, <strong>{data.mode_changes.to_local}</strong> back to local.</span>
              <span>Update checks: <strong>{data.updates.checks}</strong>, of which <strong>{data.updates.available}</strong> found a newer version.</span>
            </div>
          </section>

          {/* ===== EVENTS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Events in window</h2>
            {data.events.length === 0 ? (
              <p className={styles.emptyNotice}>No events yet.</p>
            ) : (
              <div className={styles.eventChips}>
                {data.events.map((e) => (
                  <span key={e.name} className={styles.eventChip}>
                    <code>{e.name}</code>
                    <strong>{e.count.toLocaleString()}</strong>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* ===== RECENT INSTALLS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Recent installs</h2>
            {data.recent_installs.length === 0 ? (
              <p className={styles.emptyNotice}>No installs have reported yet.</p>
            ) : (
              <div className={styles.trendTableScroll}>
                <table className={styles.miniTable}>
                  <thead>
                    <tr>
                      <th>Install</th>
                      <th>OS</th>
                      <th>Version</th>
                      <th>Mode</th>
                      <th>Account</th>
                      <th>Launches</th>
                      <th>Last heartbeat</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th>Reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_installs.map((i) => (
                      <tr key={i.install_id}>
                        <td className={styles.mono} title={i.install_id}>{i.short_id}</td>
                        <td className={styles.left}>{OS_LABELS[i.os] || i.os || '?'} {i.os_version} {i.arch && <span className={styles.dim}>{i.arch}</span>}</td>
                        <td>{i.current_version || '?'}{i.first_version && i.first_version !== i.current_version && <span className={styles.dim}> (from {i.first_version})</span>}</td>
                        <td><span className={i.mode === 'synced' ? styles.modeSynced : styles.modeLocal}>{i.mode}</span></td>
                        <td className={styles.left}>{i.user_email ? <a href={`/accounts/${i.user_id}`}>{i.user_email}</a> : <span className={styles.dim}>anonymous</span>}</td>
                        <td>{i.launch_count}</td>
                        <td>{i.last_heartbeat_date ? shortDate(i.last_heartbeat_date) : '—'}</td>
                        <td>{fmtDateTime(i.first_seen_at)}</td>
                        <td>{fmtDateTime(i.last_seen_at)}</td>
                        <td>{i.telemetry_enabled ? 'on' : <span className={styles.bad}>off</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : !loading && (
        <div className={styles.emptyNotice}>No desktop data yet. It appears once an installed copy of Studio reports in.</div>
      )}
    </div>
  );
}
